import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, CreditCard, Check, Zap, Star, Crown, Loader2, Wallet
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PayPal icon component
const PayPalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.378c2.97 0 5.27.858 6.397 2.396.53.724.828 1.53.89 2.412.066.949-.072 2.03-.408 3.217-.495 1.75-1.337 3.15-2.5 4.162-1.11.964-2.53 1.58-4.218 1.83-.357.052-.724.078-1.095.078H8.59a.768.768 0 0 0-.758.645l-.757 4.867a.64.64 0 0 1-.632.553h-3.25l.883-5.746h3.034l-.034.22zm5.557-11.83c-.05.346-.156.68-.316.998-.408.815-1.09 1.244-2.028 1.244h-1.31l.687-4.428h1.36c1.014 0 1.637.505 1.637 1.377 0 .298-.01.553-.03.809z"/>
  </svg>
);

const BuyCreditsPage = () => {
  const { isAuthenticated, wallet, refreshWallet, loading: authLoading } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // Will be set after fetching available methods
  const [paymentMethods, setPaymentMethods] = useState({
    stripe_enabled: false,
    paypal_enabled: false,
    crypto_enabled: false,
    paypal_client_id: null
  });
  const [processingPayPal, setProcessingPayPal] = useState(false);
  const [methodsLoading, setMethodsLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
    fetchPaymentMethods();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/payments/packages`);
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load credit packages');
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      setMethodsLoading(true);
      const response = await axios.get(`${API}/payments/methods`);
      setPaymentMethods(response.data);
      
      // Set default payment method to the first available one
      if (response.data.stripe_enabled) {
        setPaymentMethod('stripe');
      } else if (response.data.paypal_enabled) {
        setPaymentMethod('paypal');
      } else if (response.data.crypto_enabled) {
        setPaymentMethod('crypto');
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      // Fallback to stripe if API fails
      setPaymentMethods({ stripe_enabled: true, paypal_enabled: false, crypto_enabled: false });
      setPaymentMethod('stripe');
    } finally {
      setMethodsLoading(false);
    }
  };

  // Stripe checkout handler
  const handleStripeCheckout = async (packageId) => {
    setLoading(true);
    setSelectedPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  // PayPal create order handler
  const createPayPalOrder = async (packageId) => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        package_id: packageId
      });
      return response.data.order_id;
    } catch (error) {
      console.error('PayPal create order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create PayPal order');
      throw error;
    }
  };

  // PayPal approve handler
  const onPayPalApprove = async (data, packageId) => {
    setProcessingPayPal(true);
    try {
      const response = await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID
      });
      
      if (response.data.status === 'completed') {
        toast.success(`Payment successful! ${response.data.credits_added} credits added to your account.`);
        await refreshWallet();
        // Navigate to success page or show success state
      } else {
        toast.info(`Payment status: ${response.data.status}`);
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete PayPal payment');
    } finally {
      setProcessingPayPal(false);
    }
  };

  const getPackageIcon = (index) => {
    switch (index) {
      case 0: return <Zap className="h-8 w-8" />;
      case 1: return <Star className="h-8 w-8" />;
      case 2: return <Crown className="h-8 w-8" />;
      default: return <CreditCard className="h-8 w-8" />;
    }
  };

  const getPackageColor = (index) => {
    switch (index) {
      case 0: return 'from-blue-500 to-blue-600';
      case 1: return 'from-purple-500 to-purple-600';
      case 2: return 'from-amber-500 to-orange-600';
      default: return 'from-green-500 to-emerald-600';
    }
  };

  const getBestValue = (packages) => {
    if (packages.length === 0) return null;
    return packages.reduce((best, pkg) => 
      pkg.price_per_credit < best.price_per_credit ? pkg : best
    );
  };

  // Redirect if not authenticated
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  const bestValue = getBestValue(packages);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/account">
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="back-btn">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <CreditCard className="h-6 w-6" />
                Buy Credits
              </h1>
              <p className="text-blue-200 text-sm">Choose a credit package</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
            <Wallet className="h-5 w-5 text-yellow-400" />
            <span className="text-white font-medium">{wallet?.balance_credits?.toFixed(0) || 0} credits</span>
          </div>
        </div>

        {/* Payment Method Selector */}
        <Card className="bg-white/10 border-white/20 backdrop-blur-sm mb-8">
          <CardHeader>
            <CardTitle className="text-white">Payment Method</CardTitle>
            <CardDescription className="text-blue-200">Choose your preferred payment method</CardDescription>
          </CardHeader>
          <CardContent>
            {methodsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
                <span className="ml-2 text-blue-200">Loading payment methods...</span>
              </div>
            ) : !paymentMethods.stripe_enabled && !paymentMethods.paypal_enabled && !paymentMethods.crypto_enabled ? (
              <Alert className="bg-yellow-500/20 border-yellow-500/50 text-yellow-100">
                <AlertDescription>
                  No payment methods are currently available. Please contact support.
                </AlertDescription>
              </Alert>
            ) : (
              <Tabs value={paymentMethod} onValueChange={setPaymentMethod} className="w-full">
                <TabsList className={`grid w-full bg-white/10 ${
                  [paymentMethods.stripe_enabled, paymentMethods.paypal_enabled, paymentMethods.crypto_enabled].filter(Boolean).length === 1 
                    ? 'grid-cols-1' 
                    : [paymentMethods.stripe_enabled, paymentMethods.paypal_enabled, paymentMethods.crypto_enabled].filter(Boolean).length === 2 
                      ? 'grid-cols-2' 
                      : 'grid-cols-3'
                }`}>
                  {paymentMethods.stripe_enabled && (
                    <TabsTrigger 
                      value="stripe" 
                      className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-200"
                      data-testid="stripe-tab"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      Credit Card
                    </TabsTrigger>
                  )}
                  {paymentMethods.paypal_enabled && (
                    <TabsTrigger 
                      value="paypal" 
                      className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-200"
                      data-testid="paypal-tab"
                    >
                      <PayPalIcon className="mr-2 h-4 w-4" />
                      PayPal
                    </TabsTrigger>
                  )}
                  {paymentMethods.crypto_enabled && (
                    <TabsTrigger 
                      value="crypto" 
                      className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-200"
                      data-testid="crypto-tab"
                    >
                      <Wallet className="mr-2 h-4 w-4" />
                      Crypto
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            )}
          </CardContent>
        </Card>

        {/* Credit Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.id}
              className={`relative bg-white/10 border-white/20 backdrop-blur-sm transition-all hover:scale-105 hover:bg-white/15 ${
                pkg.id === bestValue?.id ? 'ring-2 ring-green-500' : ''
              }`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.id === bestValue?.id && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                  Best Value
                </Badge>
              )}
              <CardHeader className="text-center pb-2">
                <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-br ${getPackageColor(index)} flex items-center justify-center text-white mb-4`}>
                  {getPackageIcon(index)}
                </div>
                <CardTitle className="text-white text-xl">{pkg.name}</CardTitle>
                <CardDescription className="text-blue-200">
                  {pkg.credits.toLocaleString()} credits
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div>
                  <span className="text-4xl font-bold text-white">${pkg.price_usd}</span>
                </div>
                <div className="text-blue-300 text-sm">
                  ${(pkg.price_per_credit * 100).toFixed(2)} per 100 credits
                </div>
                <ul className="text-left text-sm text-blue-200 space-y-2">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400" />
                    Instant delivery
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400" />
                    No expiration
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400" />
                    Secure payment
                  </li>
                </ul>
                
                {/* Payment Button based on selected method */}
                {paymentMethod === 'stripe' && paymentMethods.stripe_enabled ? (
                  <Button
                    onClick={() => handleStripeCheckout(pkg.id)}
                    disabled={loading}
                    className={`w-full bg-gradient-to-r ${getPackageColor(index)} hover:opacity-90`}
                    data-testid={`buy-stripe-${pkg.id}`}
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay with Card
                      </>
                    )}
                  </Button>
                ) : paymentMethod === 'paypal' && paymentMethods.paypal_enabled && paymentMethods.paypal_client_id ? (
                  <PayPalScriptProvider options={{ 
                    clientId: paymentMethods.paypal_client_id,
                    currency: "USD"
                  }}>
                    <div className="paypal-button-container">
                      {processingPayPal ? (
                        <Button disabled className="w-full bg-[#0070ba] hover:bg-[#003087]">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </Button>
                      ) : (
                        <PayPalButtons
                          style={{ 
                            layout: "horizontal",
                            color: "blue",
                            shape: "rect",
                            label: "pay",
                            height: 40
                          }}
                          createOrder={() => createPayPalOrder(pkg.id)}
                          onApprove={(data) => onPayPalApprove(data, pkg.id)}
                          onError={(err) => {
                            console.error('PayPal error:', err);
                            toast.error('PayPal payment failed. Please try again.');
                          }}
                          onCancel={() => {
                            toast.info('Payment cancelled');
                          }}
                        />
                      )}
                    </div>
                  </PayPalScriptProvider>
                ) : paymentMethod === 'crypto' && paymentMethods.crypto_enabled ? (
                  <Button disabled className="w-full bg-gradient-to-r from-orange-500 to-yellow-500">
                    <Wallet className="mr-2 h-4 w-4" />
                    Crypto (Coming Soon)
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-gray-500">
                    Select Payment Method
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info */}
        <Alert className="bg-blue-500/20 border-blue-500/50 text-blue-100">
          <CreditCard className="h-4 w-4" />
          <AlertDescription>
            Credits are used to pay for trading fees. Payments are securely processed by {
              paymentMethod === 'stripe' ? 'Stripe' : 
              paymentMethod === 'paypal' ? 'PayPal' : 
              paymentMethod === 'crypto' ? 'Cryptocurrency' : 'our payment partners'
            }.
            Credits never expire and can be used across all supported exchanges.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default BuyCreditsPage;
